import { ADMIN_HEADER_HEIGHT } from '@src/styles/dimensions';
import type { ToastPosition, ToastPropsBase, ToastsProps } from '@talos/kyoko';
import {
  GlobalToastManager,
  GlobalToastStoreProvider,
  PRINCIPAL_MAIN_HEADER_HEIGHT,
  Toasts,
  useGlobalToastsSelector,
  Z_INDEX,
} from '@talos/kyoko';
import styled from 'styled-components';
import { useAdminHeaderContext } from '../../providers/AdminHeaderContext';
import type { AppInboxToastStateItem } from './AppInboxToast';
import { AppInboxToastContainer } from './AppInboxToast';

const globalToastManager = new GlobalToastManager('ui');
const appInboxToastManager = new GlobalToastManager('appInbox');

export type GlobalToastsProps = {
  /** Override of the toastRenderer process for testing */
  renderToastOverride?: ToastsProps['renderToast'];
};
export const GlobalToasts = (props: GlobalToastsProps) => {
  return (
    <GlobalToastStoreProvider>
      <GlobalToastsInner renderToastOverride={props.renderToastOverride} />
    </GlobalToastStoreProvider>
  );
};

const GLOBAL_TOAST_WIDTH = '350px';
const APP_INBOX_TOAST_WIDTH = '290px';

const isAppInboxToast = (toast: ToastPropsBase): toast is AppInboxToastStateItem => {
  return 'appInboxToastType' in toast;
};

export const GlobalToastsInner = ({ renderToastOverride }: { renderToastOverride?: ToastsProps['renderToast'] }) => {
  const uiToasts = useGlobalToastsSelector(state => state.toasts.uiToasts);
  const appInboxToasts = useGlobalToastsSelector(state => state.toasts.appInboxToasts);
  const { isAdminHeaderDismissed } = useAdminHeaderContext();
  const processGroups: Array<{
    type: 'global' | 'appInbox';
    toasts: ToastPropsBase[];
    position: ToastPosition;
    width: string;
    remove: ToastsProps<AppInboxToastStateItem>['remove'];
  }> = [
    {
      type: 'global',
      toasts: uiToasts,
      position: 'top-center',
      remove: id => globalToastManager.removeToast(id),
      width: GLOBAL_TOAST_WIDTH,
    },
    {
      type: 'appInbox',
      toasts: appInboxToasts,
      position: 'top-right',
      width: APP_INBOX_TOAST_WIDTH,
      // Notification state (inbox vs archive) is handled by addToast(onRemove) / AppInboxToast.
      remove: notificationId => {
        appInboxToastManager.removeToast(notificationId);
      },
    },
  ];

  return (
    <>
      {processGroups.map(({ remove, toasts, position, width }) => {
        const alignItems = position.includes('left')
          ? 'flex-start'
          : position.includes('right')
            ? 'flex-end'
            : 'center';
        const justifyContent = position.includes('top')
          ? 'flex-start'
          : position.includes('bottom')
            ? 'flex-end'
            : 'center';
        return (
          <GlobalToastsWrapper
            data-testid="global-toasts-wrapper"
            key={position}
            position={position}
            isAdminToolbarVisible={!isAdminHeaderDismissed}
          >
            <Toasts
              stackingLimit={5}
              remove={remove}
              alignItems={alignItems}
              justifyContent={justifyContent}
              toasts={toasts}
              direction="column-reverse"
              width={width}
              container="global"
              renderToast={
                renderToastOverride ?? {
                  shouldRenderToast: isAppInboxToast,
                  renderer: toast => {
                    return (
                      isAppInboxToast(toast) && (
                        <AppInboxToastContainer {...toast} wrapperWidth={width} containerVariant="toast" />
                      )
                    );
                  },
                }
              }
            />
          </GlobalToastsWrapper>
        );
      })}
    </>
  );
};

const GlobalToastsWrapper = styled.div<{ position: ToastPosition; isAdminToolbarVisible: boolean }>`
  position: fixed;
  z-index: ${Z_INDEX.globalToast};
  // 100dvw/100vw is here to ensure that the animation exit animation is smooth
  // - 100vw is a fallback for browsers that don't support dvw, which better handles mobile behavior
  width: 100vw;
  width: 100dvw;
  pointer-events: none;
  display: flex;
  align-items: center;
  ${({ position, isAdminToolbarVisible }) => {
    const offset = 8;
    const headerHeight =
      (isAdminToolbarVisible ? ADMIN_HEADER_HEIGHT + PRINCIPAL_MAIN_HEADER_HEIGHT : PRINCIPAL_MAIN_HEADER_HEIGHT) +
      offset +
      10;
    switch (position) {
      case 'top-right':
        return `
          top: ${headerHeight - offset}px;
          right: ${offset}px;
          transform: none;
        `;
      case 'top-left':
        return `
          top: ${headerHeight - offset}px;
          left: ${offset}px;
          transform: none;
        `;
      case 'bottom-center':
        return `
          bottom: ${offset}px;
          left: 50%;
          transform: translateX(-50%);
        `;
      case 'bottom-right':
        return `
          bottom: ${offset}px;
          right: ${offset}px;
          transform: none;
        `;
      case 'bottom-left':
        return `
          bottom: ${offset}px;
          left: ${offset}px;
          transform: none;
        `;
      case 'top-center':
      default:
        return `
          top: ${headerHeight - offset}px;
          left: 50%;
          transform: translateX(-50%);
        `;
    }
  }}
`;
